/**
 * Pure helpers for the shared JobFamilyLevelsTable (ER-11647, Organization > Job Families).
 *
 * Kept framework-free so unit tests can exercise row shaping and ordering without
 * mounting the SFC (mirrors the approach used by the sibling JobFamiliesList /
 * JobCategoriesList row helpers).
 *
 * Job Family Levels have no status/lifecycle (rules §6, §10.2) — a Level is either
 * there or it isn't. The API returns Levels ordered by ordinal ascending (Level 1
 * first); the table renders descending, highest ordinal at the top, Level 1 at the
 * bottom, so the visual order matches the career ladder (rules §3.1, §3.2).
 */

/** Per-row overflow menu actions. Levels have no lifecycle, so only Edit/Delete exist. */
export const ROW_ACTIONS = [
  { value: "edit", label: "Edit" },
  { value: "delete", label: "Delete", destructive: true },
];

/** A row IQ added gets a "New" badge; a row whose Descriptor IQ revised gets "Updated". */
export const LEVEL_FLAG_NEW = "new";
export const LEVEL_FLAG_UPDATED = "updated";

/**
 * Converts the orchestration endpoint's `changes` list (ER-11792) into a
 * `{ [levelId]: "new" | "updated" }` map for the table's per-row badges.
 * Expected input: `[{ level_id, kind }]`; unknown kinds are ignored.
 */
export function levelFlagsFromChanges(changes) {
  const flags = {};
  for (const change of changes || []) {
    if (
      change &&
      change.level_id != null &&
      (change.kind === LEVEL_FLAG_NEW || change.kind === LEVEL_FLAG_UPDATED)
    ) {
      flags[change.level_id] = change.kind;
    }
  }
  return flags;
}

/**
 * Map a Job Family Level API row onto the table's camelCase shape. The v2 read
 * serializer returns `position_count` (snake_case); the table consumes
 * `positionCount`. Accepts either the raw serializer payload or an already-mapped
 * row so callers can reuse it without double-mapping.
 */
export function fromApiJobFamilyLevel(apiLevel) {
  const raw = apiLevel && typeof apiLevel === "object" ? apiLevel : {};
  const positionCount = raw.position_count ?? raw.positionCount;
  return {
    id: raw.id ?? null,
    ordinal: raw.ordinal ?? null,
    name: String(raw.name || "").trim(),
    descriptor: String(raw.descriptor || "").trim(),
    positionCount: Number.isFinite(positionCount) ? positionCount : 0,
  };
}

/**
 * Builds the normalised row view consumed by JobFamilyLevelsTable.vue.
 * Expected input shape: `{ id, ordinal, name, descriptor, positionCount }`.
 */
export function buildLevelView(level) {
  const raw = level || {};
  return {
    id: raw.id ?? null,
    ordinal: raw.ordinal ?? null,
    name: String(raw.name || "").trim(),
    descriptor: String(raw.descriptor || "").trim(),
    positionCount: Number.isFinite(raw.positionCount) ? raw.positionCount : 0,
  };
}

/**
 * Orders levels for display: descending by ordinal (highest/top of the ladder
 * first, Level 1 last), per rules §3.2. Levels missing an ordinal are pushed to
 * the end in their original relative order rather than sorted arbitrarily.
 */
export function buildDisplayOrder(levels) {
  const views = (levels || []).map((level) => buildLevelView(level));
  return [...views].sort((a, b) => {
    if (a.ordinal === null && b.ordinal === null) return 0;
    if (a.ordinal === null) return 1;
    if (b.ordinal === null) return -1;
    return b.ordinal - a.ordinal;
  });
}

/**
 * Moves `sourceId` to just before/after `targetId` in the given ordinal-ascending
 * id list (Level 1 first) and returns the new dense-renumbered ascending id list.
 * Used to compute the optimistic reorder result and the payload sent upstream.
 */
export function reorderAscendingIds(ascendingIds, sourceId, targetId) {
  const ids = [...(ascendingIds || [])];
  const from = ids.indexOf(sourceId);
  const to = ids.indexOf(targetId);
  if (from === -1 || to === -1 || from === to) return ids;
  const [moved] = ids.splice(from, 1);
  ids.splice(to, 0, moved);
  return ids;
}
